<template>
  <div class="space-y-6">
    <!-- 헤더 및 그룹 목록 -->
    <div class="card-base p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="page-title">그룹 관리</h2>
          <p class="page-subtitle">크레딧 채권 발행사 그룹을 관리합니다</p>
        </div>
      </div>

      <!-- 크레딧 타입 선택과 그룹 추가 버튼 -->
      <div class="mb-6 flex items-center justify-between">
        <div class="flex flex-wrap gap-2">
          <button v-for="(name, type) in creditTypes" :key="type" @click="selectCreditType(type)" :class="[
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            currentCreditType === type
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          ]">
            {{ name }}
          </button>
        </div>

        <BaseButton variant="primary" icon="fas fa-plus" @click="handleOpenCreateModal">
          그룹 추가
        </BaseButton>
      </div>

      <!-- 그룹 목록 -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <div class="loading-spinner"></div>
        <span class="ml-3 text-gray-600">그룹 목록을 불러오는 중...</span>
      </div>

      <div v-else-if="currentGroups.length === 0" class="text-center py-12 text-gray-500">
        <i class="fas fa-layer-group text-4xl mb-4"></i>
        <p>등록된 그룹이 없습니다.</p>
        <p class="text-sm mt-2">새로운 그룹을 추가해보세요.</p>
      </div>

      <!-- 그룹 카드 그리드 -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <div v-for="group in currentGroups" :key="group.id"
          class="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
          <!-- 카드 헤더 -->
          <div class="p-4">
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1">
                <h3 class="text-base font-medium text-gray-900 mb-2">
                  {{ group.name }}
                </h3>
                <div class="flex items-center text-sm text-gray-500">
                  <i class="fas fa-building mr-1"></i>
                  <span>{{ group.issuers?.length || 0 }}개 발행사</span>
                </div>
              </div>

              <!-- 액션 버튼들 -->
              <div class="flex space-x-1">
                <button @click="handleOpenEditModal(group)"
                  class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
                  title="그룹 수정">
                  <i class="fas fa-edit text-sm"></i>
                </button>
                <button @click="confirmDeleteGroup(group)"
                  class="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="그룹 삭제">
                  <i class="fas fa-trash text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- 포함된 발행사 목록 -->
          <div class="border-t border-gray-200 bg-gray-50 p-4">
            <div v-if="!group.issuers || group.issuers.length === 0" class="text-center py-4 text-gray-500">
              <i class="fas fa-building text-xl mb-2"></i>
              <p class="text-sm">포함된 발행사가 없습니다.</p>
            </div>
            <div v-else class="space-y-2">
              <h4 class="text-sm font-medium text-gray-700 mb-3">포함된 발행사</h4>
              <div class="space-y-2">
                <div v-for="issuer in group.issuers" :key="issuer.id"
                  class="flex items-center justify-between bg-white p-3 rounded border border-gray-200">
                  <span class="text-sm font-medium text-gray-900">{{ issuer.name }}</span>
                  <span class="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                    {{ issuer.grade }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 그룹 생성 모달 -->
    <BaseModal v-model="modals.create" title="그룹 추가" size="lg" @close="handleCloseCreateModal">
      <div class="space-y-4">
        <BaseInput v-model="createForm.name" label="그룹명" placeholder="그룹명을 입력하세요" required />

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">크레딧 유형</label>
          <select v-model="createForm.creditType" class="select-base" required>
            <option value="">유형을 선택하세요</option>
            <option v-for="(name, type) in creditTypes" :key="type" :value="type">
              {{ name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">포함할 발행사</label>
          <div v-if="availableIssuers.length === 0" class="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">
            선택한 크레딧 유형에 등록된 발행사가 없습니다.
          </div>
          <div v-else class="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
            <label v-for="issuer in availableIssuers" :key="issuer.id"
              class="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
              <input type="checkbox" :value="issuer.id" v-model="createForm.issuerIds"
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              <div class="flex-1">
                <span class="text-sm font-medium text-gray-900">{{ issuer.name }}</span>
                <span class="text-xs text-gray-500 ml-2">({{ issuer.grade }})</span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <template #footer>
        <BaseButton variant="outline" @click="handleCloseCreateModal">
          취소
        </BaseButton>
        <BaseButton variant="primary" @click="handleCreate" :loading="isCreating">
          추가
        </BaseButton>
      </template>
    </BaseModal>

    <!-- 그룹 수정 모달 -->
    <BaseModal v-model="modals.edit" title="그룹 수정" size="lg" @close="handleCloseEditModal">
      <div class="space-y-4">
        <BaseInput v-model="editForm.name" label="그룹명" required />

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">포함할 발행사</label>
          <div v-if="availableIssuers.length === 0" class="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">
            해당 크레딧 유형에 등록된 발행사가 없습니다.
          </div>
          <div v-else class="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
            <label v-for="issuer in availableIssuers" :key="issuer.id"
              class="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
              <input type="checkbox" :value="issuer.id" v-model="editForm.issuerIds"
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              <div class="flex-1">
                <span class="text-sm font-medium text-gray-900">{{ issuer.name }}</span>
                <span class="text-xs text-gray-500 ml-2">({{ issuer.grade }})</span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <template #footer>
        <BaseButton variant="outline" @click="handleCloseEditModal">
          취소
        </BaseButton>
        <BaseButton variant="primary" @click="handleUpdate" :loading="isUpdating">
          저장
        </BaseButton>
      </template>
    </BaseModal>

    <!-- 로딩 오버레이 -->
    <div v-if="isCreating || isUpdating || isDeleting" class="loading-overlay">
      <div class="bg-white rounded-xl p-8 shadow-2xl">
        <div class="flex items-center space-x-4">
          <div class="loading-spinner"></div>
          <span class="text-gray-700 font-medium">처리 중...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseModal from '@/components/common/BaseModal.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import { useCreditBonds } from '@/composables/useCreditBonds';
import { useGroupManagement } from '@/composables/useGroupManagement';
import {
  useFormValidation,
  useErrorHandling
} from '@/composables/useCreditManagement';

export default {
  name: 'CreditGroupComponent',
  components: {
    BaseButton,
    BaseModal,
    BaseInput,
  },
  props: {
    selectedDate: {
      type: String,
      required: true,
    },
  },

  setup() {
    const {
      isLoading,
      typeNames,
      issuersData,
      groupsByType,
      loadCreditData,
      loadGroups,
      createGroup,
      updateGroup,
      deleteGroup,
    } = useCreditBonds();

    const {
      modals,
      selectedGroup,
      isCreating,
      isUpdating,
      isDeleting,
      openModal,
      closeModal,
    } = useGroupManagement();

    const { validateRequired, showValidationError } = useFormValidation();
    const { handleAsyncOperation } = useErrorHandling();

    // 현재 크레딧 타입
    const currentCreditType = ref('PUBLIC');

    // 폼 상태
    const createForm = ref({
      name: '',
      creditType: '',
      issuerIds: [],
    });

    const editForm = ref({
      name: '',
      issuerIds: [],
    });

    // Computed
    const creditTypes = computed(() => typeNames.value || {});
    const issuers = computed(() => issuersData.value || {});
    const groups = computed(() => groupsByType.value || {});

    const currentGroups = computed(() => {
      return groups.value[currentCreditType.value] || [];
    });

    const availableIssuers = computed(() => {
      const creditType = modals.value.create
        ? createForm.value.creditType
        : currentCreditType.value;
      return issuers.value[creditType] || [];
    });

    // Watchers
    watch(
      () => createForm.value.creditType,
      () => {
        createForm.value.issuerIds = [];
      }
    );

    // Methods
    const selectCreditType = async (type) => {
      currentCreditType.value = type;
      await loadGroups(type);
    };

    const handleOpenCreateModal = () => {
      createForm.value = {
        name: '',
        creditType: currentCreditType.value,
        issuerIds: [],
      };
      openModal('create');
    };

    const handleCloseCreateModal = () => {
      closeModal('create');
    };

    const handleCreate = async () => {
      const validation = validateRequired(createForm.value, ['name', 'creditType']);

      if (!validation.isValid) {
        showValidationError(validation.missingFields, {
          name: '그룹명',
          creditType: '크레딧 유형'
        });
        return;
      }

      await handleAsyncOperation(
        async () => {
          await createGroup({
            ...createForm.value,
            type: createForm.value.creditType
          });
          handleCloseCreateModal();
          await loadGroups();
        },
        isCreating,
        '그룹 생성 중 오류가 발생했습니다.'
      );
    };

    const handleOpenEditModal = (group) => {
      editForm.value = {
        name: group.name,
        issuerIds: group.issuers
          ? group.issuers.map(issuer => issuer.id)
          : [],
      };
      openModal('edit', group);
    };

    const handleCloseEditModal = () => {
      closeModal('edit');
    };

    const handleUpdate = async () => {
      const validation = validateRequired(editForm.value, ['name']);

      if (!validation.isValid) {
        showValidationError(validation.missingFields, {
          name: '그룹명'
        });
        return;
      }

      await handleAsyncOperation(
        async () => {
          await updateGroup(selectedGroup.value.id, {
            ...editForm.value,
            type: currentCreditType.value
          });
          handleCloseEditModal();
          await loadGroups();
        },
        isUpdating,
        '그룹 수정 중 오류가 발생했습니다.'
      );
    };

    const confirmDeleteGroup = (group) => {
      if (window.confirm(`'${group.name}' 그룹을 삭제하시겠습니까?`)) {
        handleDelete(group);
      }
    };

    const handleDelete = async (group) => {
      await handleAsyncOperation(
        async () => {
          await deleteGroup(group.id);
          await loadGroups();
        },
        isDeleting,
        '그룹 삭제 중 오류가 발생했습니다.'
      );
    };

    onMounted(async () => {
      await loadCreditData();
      await loadGroups(currentCreditType.value);
    });

    return {
      // Data
      isLoading,
      creditTypes,
      currentCreditType,
      currentGroups,
      availableIssuers,

      // Modal states
      modals,
      selectedGroup,
      isCreating,
      isUpdating,
      isDeleting,
      createForm,
      editForm,

      // Methods
      selectCreditType,
      handleOpenCreateModal,
      handleCloseCreateModal,
      handleCreate,
      handleOpenEditModal,
      handleCloseEditModal,
      handleUpdate,
      confirmDeleteGroup,
    };
  },
};
</script>
